import { useQuery } from "react-query";
import HttpService from "../../services/http";
import {
  PickedUser,
  PickedGroup,
} from "../../components/pages/dashboard/todos/AssigneePicker";

export const useResolveAssignees = (
  customerId: string,
  entityId: string,
  userIds: string[],
  groupIds: string[]
) => {
  const uniqueUserIds = Array.from(new Set(userIds)).sort();
  const uniqueGroupIds = Array.from(new Set(groupIds)).sort();

  const usersQuery = useQuery<Record<string, PickedUser>>(
    ["todo-resolve-users", entityId, uniqueUserIds.join(",")],
    async () => {
      const entries = await Promise.all(
        uniqueUserIds.map(async (id) => {
          try {
            const res = await HttpService.get(
              `/${customerId}/${entityId}/user/${id}/profile`
            );
            const u = res?.data?.element || {};
            const name =
              [u.user_first_name, u.user_last_name].filter(Boolean).join(" ") ||
              u.user_user_name ||
              u.user_email ||
              "";
            const resolved: PickedUser = {
              id,
              firstName: u.user_first_name,
              lastName: u.user_last_name,
              email: u.user_email,
              avatar: u.user_avatar || undefined,
              name,
              role: u.user_role,
            };
            return [id, resolved] as const;
          } catch {
            return [id, { id, name: "" }] as const;
          }
        })
      );
      return Object.fromEntries(entries);
    },
    {
      enabled: !!customerId && !!entityId && uniqueUserIds.length > 0,
      keepPreviousData: true,
    }
  );

  const groupsQuery = useQuery<Record<string, PickedGroup>>(
    ["todo-resolve-groups", entityId, uniqueGroupIds.join(",")],
    async () => {
      const entries = await Promise.all(
        uniqueGroupIds.map(async (id) => {
          try {
            const res = await HttpService.get(
              `/${customerId}/${entityId}/group/${id}`
            );
            const g = res?.data?.element || {};
            return [id, { id, name: g.group_name || "" }] as const;
          } catch {
            return [id, { id, name: "" }] as const;
          }
        })
      );
      return Object.fromEntries(entries);
    },
    {
      enabled: !!customerId && !!entityId && uniqueGroupIds.length > 0,
      keepPreviousData: true,
    }
  );

  return {
    resolvedUsers: usersQuery.data || {},
    resolvedGroups: groupsQuery.data || {},
    isResolving: usersQuery.isLoading || groupsQuery.isLoading,
  };
};
